import { useEffect, useRef, createRef, useMemo } from 'react';

import { Grid, FormControl, MenuItem, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { FormikProps } from 'formik';
import { getFormikInputProps } from '../../../helpers/form';
import { BaseInput } from 'src/components/ui/form/BaseInput';

import FilterLayout from './FilterLayout';

import {
  PortfolioFilter,
  FilterLabels,
  FilterViewType,
  FilterView,
  PortfolioFilterOptions,
} from '../../../state/contexts/portfolioPage';
import { onlineStatusOptions, signalStrengthOptions, SelectOption } from './filterOptionsMock';
import { BaseSelect } from 'src/components/ui/form/BaseSelect';
interface FiltersProps {
  formik: FormikProps<PortfolioFilter>;
  selectedFilterViews: FilterViewType[];
  lastFilterAdded: FilterViewType | null;
  setLastFilterAdded: (filterView: FilterViewType | null) => void;
  handleRemoveChip: (keys: (keyof PortfolioFilter)[], filterView?: FilterViewType) => void;
  filterOptions: PortfolioFilterOptions;
  orderOfFilters: FilterViewType[];
}

const Filters = ({
  formik,
  selectedFilterViews,
  lastFilterAdded,
  setLastFilterAdded,
  handleRemoveChip,
  filterOptions,
  orderOfFilters,
}: FiltersProps) => {
  const classes = useStyles();
  const firmwareVersions = useMemo(() => {
    const type = formik.values.equipmentType || '';

    const result =
      filterOptions?.equipmentFirmwareVersions
        .filter((e) => e.Model === type)
        .map((e) => e.FirmwareVersion)
        .sort() || [];

    return result;
  }, [formik.values.equipmentType, filterOptions?.equipmentFirmwareVersions]);

  const filterRefs = useRef({
    [FilterView.endUserNameAndAddress]: createRef<HTMLDivElement>(),
    [FilterView.installerCompanyName]: createRef<HTMLDivElement>(),
    [FilterView.equipmentType]: createRef<HTMLDivElement>(),
    [FilterView.equipmentSerialNumber]: createRef<HTMLDivElement>(),
    [FilterView.equipmentFirmwareVersion]: createRef<HTMLDivElement>(),
    [FilterView.onlineStatus]: createRef<HTMLDivElement>(),
    [FilterView.signalStrength]: createRef<HTMLDivElement>(),
  });

  useEffect(() => {
    if (lastFilterAdded) {
      filterRefs.current[
        lastFilterAdded as keyof typeof filterRefs.current
      ]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setLastFilterAdded(null);
    }
  }, [lastFilterAdded, setLastFilterAdded]);

  const renderFilter = (filterType: FilterViewType) => {
    switch (filterType) {
      // ADD FILTERS
      case FilterView.endUserNameAndAddress:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.endUserNameAndAddress]}
            isVisible={selectedFilterViews.includes(FilterView.endUserNameAndAddress)}
            title={FilterLabels[FilterView.endUserNameAndAddress]!}
            onClose={() => {
              handleRemoveChip(['endUserName', 'endUserAddress'], FilterView.endUserNameAndAddress);
            }}
          >
            <Grid container item spacing={2}>
              <Grid item xs={12}>
                <BaseInput
                  fullWidth
                  variant="outlined"
                  label="End user name"
                  id="endUserName"
                  value={formik.values.endUserName}
                  {...getFormikInputProps(formik, 'endUserName', 'text')}
                  onChange={(e) => {
                    formik.setFieldValue('endUserName', e.target.value || undefined);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <BaseInput
                  fullWidth
                  variant="outlined"
                  label="End user address"
                  id="endUserAddress"
                  value={formik.values.endUserAddress}
                  {...getFormikInputProps(formik, 'endUserAddress', 'text')}
                  onChange={(e) => {
                    formik.setFieldValue('endUserAddress', e.target.value || undefined);
                  }}
                />
              </Grid>
            </Grid>
          </FilterLayout>
        );
      case FilterView.installerCompanyName:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.installerCompanyName]}
            isVisible={selectedFilterViews.includes(FilterView.installerCompanyName)}
            title={FilterLabels[FilterView.installerCompanyName]!}
            onClose={() => {
              handleRemoveChip(['installerCompanyName'], FilterView.installerCompanyName);
            }}
          >
            <Grid container item xs={12}>
              <BaseSelect
                id="installerCompanyName"
                placeholder="Select installer"
                value={formik.values.installerCompanyName || ''}
                onChange={(e) => formik.setFieldValue('installerCompanyName', e.target.value)}
                displayEmpty
              >
                <MenuItem key="installerCompanyName" value="" disabled>
                  Select installer
                </MenuItem>
                {filterOptions?.installers.map((option: { name: string; value: string }) => (
                  <MenuItem key={option.value.replace(/ /g, '_')} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </BaseSelect>
            </Grid>
          </FilterLayout>
        );
      case FilterView.equipmentType:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.equipmentType]}
            isVisible={selectedFilterViews.includes(FilterView.equipmentType)}
            title={FilterLabels[FilterView.equipmentType]!}
            onClose={() => {
              handleRemoveChip(['equipmentType'], FilterView.equipmentType);
            }}
            error={formik.errors.equipmentType as string}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <BaseSelect
                    id="equipmentType"
                    value={formik.values.equipmentType || ''}
                    onChange={(e) => formik.setFieldValue('equipmentType', e.target.value)}
                  >
                    {filterOptions?.equipmentTypes?.map(
                      (option: { name: string; value: string }) => (
                        <MenuItem key={option.value.replace(/ /g, '_')} value={option.value}>
                          {option.name}
                        </MenuItem>
                      )
                    )}
                  </BaseSelect>
                </FormControl>
              </Grid>

              {/* {formik.values.equipmentType && formik.values.equipmentType.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="body1" classes={{ root: classes.body1 }}>
                    How many?
                  </Typography>
                </Grid> */}
              {/* <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel htmlFor="deviceQuantityOperator">Conditional</InputLabel>
                    <BaseSelect
                      id="equipmentQuantityOperator"
                      label="Conditional"
                      value={formik.values.equipmentQuantityOperator || ''}
                      onChange={(e) =>
                        formik.setFieldValue('equipmentQuantityOperator', e.target.value)
                      }
                    >
                      {equipmentQuantityOperatorOptions.map((option: SelectOption) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </BaseSelect>
                  </FormControl>
                </Grid> */}
              {/* <Grid item xs={12}>
                  <BaseInput
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Amount"
                    type="number"
                    value={formik.values.equipmentQuantity || ''}
                    onChange={(e) => formik.setFieldValue('equipmentQuantity', e.target.value)}
                  />
                </Grid> */}
              {/* </>
            )} */}
            </Grid>
          </FilterLayout>
        );
      case FilterView.equipmentSerialNumber:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.equipmentSerialNumber]}
            isVisible={selectedFilterViews.includes(FilterView.equipmentSerialNumber)}
            title={FilterLabels[FilterView.equipmentSerialNumber]!}
            onClose={() => {
              handleRemoveChip(
                // Add 'serialNumberOperator' key to the array when conditional is added
                ['equipmentSerialNumber'],
                FilterView.equipmentSerialNumber
              );
            }}
            error={formik.errors.equipmentSerialNumber as string}
          >
            <Grid container spacing={2}>
              {/* Add this when the conditional field is implemented in the backend */}
              {/* <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel htmlFor="serialNumberOperator">Conditional</InputLabel>
                <BaseSelect
                  id="serialNumberOperator"
                  fullWidth
                  variant="outlined"
                  label="Conditional"
                  value={formik.values.serialNumberOperator || ''}
                  onChange={(e) => formik.setFieldValue('serialNumberOperator', e.target.value)}
                >
                  {serialNumberOperatorOptions.map((option: SelectOption) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  classes={{ root: classes.body1 }}
                  style={{ margin: '8px 0 8px 0' }}
                >
                  Type in the serial number
                </Typography>
                <BaseInput
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Type in serial number"
                  value={formik.values.equipmentSerialNumber || ''}
                  onChange={(e) => formik.setFieldValue('equipmentSerialNumber', e.target.value)}
                />
              </Grid>
            </Grid>
          </FilterLayout>
        );
      case FilterView.equipmentFirmwareVersion:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.equipmentFirmwareVersion]}
            isVisible={selectedFilterViews.includes(FilterView.equipmentFirmwareVersion)}
            title={FilterLabels[FilterView.equipmentFirmwareVersion]!}
            onClose={() => {
              handleRemoveChip(
                // Add 'equipmentFirmwareVersionOperator' key to the array when conditional is added
                ['equipmentFirmwareVersion', 'equipmentType'],
                FilterView.equipmentFirmwareVersion
              );
            }}
            error={formik.errors.equipmentFirmwareVersion as string}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BaseSelect
                  labelId="equipment-id"
                  id="equipmentType"
                  value={formik.values.equipmentType || ''}
                  onChange={(e) => formik.setFieldValue('equipmentType', e.target.value)}
                  label="Equipment Type"
                >
                  {filterOptions?.equipmentTypes?.map((option: { name: string; value: string }) => (
                    <MenuItem key={option.value.replace(/ /g, '_')} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </BaseSelect>
              </Grid>
              {formik.values.equipmentType && formik.values.equipmentType.length > 0 && (
                <>
                  {/* Add this when conditional field is implemented in the backend  */}
                  {/* <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel htmlFor="equipmentFirmwareVersionOperator">Conditional</InputLabel>
                      <BaseSelect
                        id="equipmentFirmwareVersionOperator"
                        fullWidth
                        variant="outlined"
                        label="Version is"
                        value={formik.values.equipmentFirmwareVersionOperator || ''}
                        placeholder="Select conditional"
                        onChange={(e) =>
                          formik.setFieldValue('equipmentFirmwareVersionOperator', e.target.value)
                        }
                      >
                        {equipmentFirmwareVersionOperatorOptions.map((option: SelectOption) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </BaseSelect>
                    </FormControl>
                  </Grid> */}
                  {firmwareVersions.length > 0 ? (
                    <Grid item xs={12}>
                      <BaseSelect
                        labelId="equipmentFirmwareVersion"
                        id="equipmentFirmwareVersion"
                        fullWidth
                        variant="outlined"
                        label="Version"
                        value={formik.values.equipmentFirmwareVersion || ''}
                        onChange={(e) =>
                          formik.setFieldValue('equipmentFirmwareVersion', e.target.value)
                        }
                      >
                        {firmwareVersions.map((option: string) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </BaseSelect>
                    </Grid>
                  ) : (
                    <Typography className={classes.firmwareVersion}>
                      No firmware versions found for this equipment type. Please select a different
                      equipment type.
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          </FilterLayout>
        );
      case FilterView.onlineStatus:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.onlineStatus]}
            isVisible={selectedFilterViews.includes(FilterView.onlineStatus)}
            title={FilterLabels[FilterView.onlineStatus]!}
            onClose={() => {
              handleRemoveChip(['onlineStatus'], FilterView.onlineStatus);
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <BaseSelect
                    id="onlineStatus"
                    fullWidth
                    variant="outlined"
                    value={formik.values.onlineStatus || ''}
                    onChange={(e) => formik.setFieldValue('onlineStatus', e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select online/offline status
                    </MenuItem>
                    {onlineStatusOptions.map((option: SelectOption) => (
                      <MenuItem key={option.value.replace(/ /g, '_')} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </BaseSelect>
                </FormControl>
              </Grid>
            </Grid>
          </FilterLayout>
        );
      case FilterView.signalStrength:
        return (
          <FilterLayout
            key={filterType}
            ref={filterRefs.current[FilterView.signalStrength]}
            isVisible={selectedFilterViews.includes(FilterView.signalStrength)}
            title={FilterLabels[FilterView.signalStrength]!}
            onClose={() => {
              handleRemoveChip(['signalStrength'], FilterView.signalStrength);
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <BaseSelect
                    id="signalStrength"
                    fullWidth
                    variant="outlined"
                    value={formik.values.signalStrength || ''}
                    onChange={(e) => formik.setFieldValue('signalStrength', e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select signal strength
                    </MenuItem>
                    {signalStrengthOptions.map((option: SelectOption) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </BaseSelect>
                </FormControl>
              </Grid>
            </Grid>
          </FilterLayout>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {orderOfFilters.length ? (
        orderOfFilters.map((filterType) => renderFilter(filterType))
      ) : (
        <Grid container item alignItems="center" spacing={1}>
          <Grid item style={{ flex: 1 }}>
            <Typography variant="h6" classes={{ root: classes.h6 }}>
              No filters added
            </Typography>
            <Typography variant="body1" classes={{ root: classes.body1 }}>
              Your selected filter will appear here
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme>((theme) => ({
  h6: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '0.15px',
  },
  body1: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
  },
  firmwareVersion: {
    fontSize: 14,
    padding: 8,
  },
}));

export default Filters;
